import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import CancelIcon from '@mui/icons-material/Cancel';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';

// PlansPanel — sub-tab under the Analysis page that lists every
// plan run the backend has accepted (analyser meta, snapshot diff,
// future ad-hoc), shows a one-line summary per plan, and opens a
// detail dialog for live monitoring.
//
// Live updates use Server-Sent Events as the primary transport —
// the backend pushes a snapshot every time the watcher mutates a
// running plan, so the operator sees verified/mismatched
// transitions the moment they happen instead of waiting up to a
// full poll cycle. If SSE fails to open (proxy stripped the
// connection, browser doesn't expose EventSource, etc.) we fall
// back to polling on POLL_INTERVAL_MS so the dialog still keeps
// up.

const STATUS_META = {
  running: { label: 'Running', color: 'info', icon: <HourglassTopIcon fontSize="small" /> },
  verified: { label: 'Verified', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  partial: { label: 'Partial', color: 'warning', icon: <ErrorIcon fontSize="small" /> },
  timed_out: { label: 'Timed out', color: 'warning', icon: <TimerOffIcon fontSize="small" /> },
  failed: { label: 'Failed', color: 'error', icon: <CancelIcon fontSize="small" /> },
  cancelled: { label: 'Cancelled', color: 'default', icon: <StopCircleIcon fontSize="small" /> },
};

const TERMINAL_STATUSES = new Set(['verified', 'partial', 'timed_out', 'failed', 'cancelled']);
const RETRIABLE_ACTION_STATUSES = new Set(['mismatched', 'timed_out', 'failed_to_send', 'cancelled']);

const ACTION_STATUS_META = {
  queued: { color: 'default', label: 'Queued' },
  sent: { color: 'info', label: 'Sent' },
  awaiting_state: { color: 'info', label: 'Awaiting state' },
  verified: { color: 'success', label: 'Verified' },
  mismatched: { color: 'warning', label: 'Mismatched' },
  timed_out: { color: 'warning', label: 'Timed out' },
  failed_to_send: { color: 'error', label: 'Failed to send' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

const KIND_LABELS = {
  'set-parent': 'Reparent',
  'set-ap-channel': 'Set channel',
  'treemgmt-patch': 'Tree config',
};

// Polling cadence for the plan-detail dialog (and the plans list)
// when the SSE live stream is unavailable. Plan watcher updates on
// the backend tick on the order of seconds, but each poll round-trips
// through Apigee → backend → Firestore, so 30 s is a much friendlier
// default that still keeps the UI usefully fresh. SSE remains the
// primary transport — when it's connected the operator sees state
// transitions the moment they happen and this poll cadence is
// effectively irrelevant.
const POLL_INTERVAL_MS = 30000;

// Go marshals an unset `time.Time{}` to "0001-01-01T00:00:00Z" — a
// non-empty truthy string that JS will happily parse to a real (very
// negative) Date. Without filtering, every freshly-created action row
// rendered "Updated 739730d ago" until the watcher published its
// first observation. We treat any timestamp before 2001-01-01 as
// "unset" so the UI shows an em-dash / nothing instead. We also
// short-circuit on the literal Go zero string so we never even
// instantiate a Date for the common case.
const GO_ZERO_TIMESTAMP = '0001-01-01T00:00:00Z';
const MEANINGFUL_TIMESTAMP_FLOOR_MS = Date.UTC(2001, 0, 1);

const isMeaningfulTimestamp = (iso) => {
  if (!iso) return false;
  if (typeof iso === 'string') {
    if (iso === GO_ZERO_TIMESTAMP) return false;
    if (iso.startsWith('0001-')) return false;
  }
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return t >= MEANINGFUL_TIMESTAMP_FLOOR_MS;
};

const formatTimestamp = (iso) => {
  if (!isMeaningfulTimestamp(iso)) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const formatRelative = (iso) => {
  if (!isMeaningfulTimestamp(iso)) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// pickFirstMeaningfulTimestamp walks an ordered list of candidates and
// returns the first one that passes isMeaningfulTimestamp. Lets the
// "Updated" column fall back gracefully through last_observed_at →
// verified_at → completed_at without surfacing zero-time values from
// fields the watcher hasn't populated yet.
const pickFirstMeaningfulTimestamp = (...candidates) => {
  for (const c of candidates) {
    if (isMeaningfulTimestamp(c)) return c;
  }
  return null;
};

// Pretty-print an arbitrary expectation value (string / number /
// null) so the table shows "newparent" not "[object Object]" when
// the watcher hasn't observed anything yet.
const formatValue = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length === 0 ? '—' : v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

// describeAction renders the human-readable headline for a row in
// the detail dialog. The action.kind is the source of truth — it's
// derived from the analyser endpoint at create time and persisted
// alongside the dispatched action.
const describeAction = (action) => {
  const kindLabel = KIND_LABELS[action.kind] || action.kind || 'Action';
  const target = action.serial || '—';
  const payload = action.payload || {};
  if (action.kind === 'set-parent') {
    const parents = payload.whitelist_parents;
    const targetLabel = Array.isArray(parents) && parents.length > 0
      ? parents.join(', ')
      : '?';
    return `${kindLabel}: ${target} → ${targetLabel}`;
  }
  if (action.kind === 'set-ap-channel') {
    // payload.channel_groups carries the primary + alternates the
    // optimizer asked for (so the device's ACS can fall back).
    // Extract the band's list and surface the alternates inline.
    const groupsObj = payload.channel_groups;
    let altsSuffix = '';
    if (groupsObj && typeof groupsObj === 'object') {
      const bandKey = Object.keys(groupsObj).find((k) => Array.isArray(groupsObj[k]) && groupsObj[k].length > 1);
      if (bandKey) {
        const list = groupsObj[bandKey];
        const alts = list.slice(1);
        if (alts.length > 0) altsSuffix = ` (ACS fallback: ${alts.join(', ')})`;
      }
    }
    return `${kindLabel}: ${target} ${payload.band || ''} → ch ${payload.channel ?? '?'}${altsSuffix}`;
  }
  if (action.kind === 'treemgmt-patch') {
    const tree = (payload.trees || [])[0] || {};
    const bits = [];
    if (tree.tree_id) bits.push(tree.tree_id);
    if (tree.tx_power_down !== undefined) bits.push(`txp ${tree.tx_power_down}`);
    if (tree.channel !== undefined) bits.push(`ch ${tree.channel}`);
    return `${kindLabel}: ${target} ${bits.join(' ')}`.trim();
  }
  return `${kindLabel}: ${target}`;
};

const PlansPanel = ({ initialPlanId, onClearInitialPlan, onOpenPlan, allowPlanDelete = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [activePlan, setActivePlan] = useState(null);
  const [activePlanId, setActivePlanId] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  // ID of the plan currently being deleted so the row's icon can show
  // a spinner without locking the rest of the table. Null when no
  // delete is in flight.
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [bulkSelection, setBulkSelection] = useState([]);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  // 'sse' = stream connected, 'polling' = SSE failed/disabled and we're
  // polling, 'idle' = no detail open. Surfaced to the operator with a
  // small chip so they know which transport is feeding them.
  const [transportMode, setTransportMode] = useState('idle');

  // Refs for the polling loop and SSE subscription. We keep them in
  // refs so unmounts, dialog closes, and transport hand-offs cancel
  // cleanly without stale-state bugs.
  const pollTimerRef = useRef(null);
  const sseUnsubscribeRef = useRef(null);
  // sseFirstSnapshotTimerRef holds the 8s "no first snapshot, fall
  // back to polling" timer started inside startStream. We track it
  // so that closing the dialog (or starting another stream before
  // the timer fires) doesn't leave a zombie callback that ends up
  // poll-fetching a plan the user has already moved on from.
  const sseFirstSnapshotTimerRef = useRef(null);
  const activePlanIdRef = useRef(null);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await api.listPlanRuns();
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to load plan runs', { variant: 'error' });
    } finally {
      setLoadingList(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { refreshList(); }, [refreshList]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    if (sseFirstSnapshotTimerRef.current) {
      clearTimeout(sseFirstSnapshotTimerRef.current);
      sseFirstSnapshotTimerRef.current = null;
    }
    if (sseUnsubscribeRef.current) {
      try { sseUnsubscribeRef.current(); } catch (e) { /* ignore */ }
      sseUnsubscribeRef.current = null;
    }
  }, []);

  const stopAllTransports = useCallback(() => {
    stopPolling();
    stopStream();
  }, [stopPolling, stopStream]);

  const fetchDetail = useCallback(async (planId, { silent = false } = {}) => {
    if (!planId) return null;
    if (!silent) setLoadingDetail(true);
    try {
      const detail = await api.getPlanRun(planId);
      if (activePlanIdRef.current === planId) {
        setActivePlan(detail);
      }
      return detail;
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to load plan run', { variant: 'error' });
      return null;
    } finally {
      if (!silent) setLoadingDetail(false);
    }
  }, [enqueueSnackbar]);

  // Polling fallback: identical to the previous behaviour but only
  // engaged after SSE failed to open, OR while the dialog is showing
  // a terminal plan that doesn't need a live stream. Self-stops on
  // terminal status and refreshes the list view so the row reflects
  // the final outcome.
  const scheduleNextPoll = useCallback((detail) => {
    if (!detail || TERMINAL_STATUSES.has(detail.status) || detail.status !== 'running') {
      stopPolling();
      refreshList();
      return;
    }
    stopPolling();
    pollTimerRef.current = setTimeout(async () => {
      if (activePlanIdRef.current !== detail.id) return;
      const next = await fetchDetail(detail.id, { silent: true });
      scheduleNextPoll(next);
    }, POLL_INTERVAL_MS);
  }, [stopPolling, refreshList, fetchDetail]);

  // startPolling is the explicit fallback path. We kick off an
  // immediate detail fetch so the table catches up instantly even
  // if the previous transport delivered an older snapshot.
  const startPolling = useCallback(async (planId) => {
    setTransportMode('polling');
    const detail = await fetchDetail(planId, { silent: true });
    scheduleNextPoll(detail);
  }, [fetchDetail, scheduleNextPoll]);

  // Open an SSE subscription for `planId`. The first snapshot replaces
  // whatever we initially fetched; subsequent ones flow live. Terminal
  // events trigger a final list refresh and tear the stream down. On
  // any transport-level error we degrade to polling — the operator
  // never sees a frozen dialog.
  const startStream = useCallback((planId) => {
    stopStream();
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
      startPolling(planId);
      return;
    }
    let opened = false;
    const unsub = api.subscribePlanRun(planId, {
      onSnapshot: (snap) => {
        opened = true;
        setTransportMode('sse');
        if (activePlanIdRef.current === planId) {
          setActivePlan(snap);
        }
      },
      onTerminal: (snap) => {
        if (activePlanIdRef.current === planId) {
          setActivePlan(snap);
        }
        stopStream();
        setTransportMode('idle');
        refreshList();
      },
      onError: () => {
        // Transient drop while we already had data: swap to polling
        // so the UI keeps making progress. Drop before any data:
        // SSE never made it through the proxy, polling is the only
        // option. Either way the user sees the "polling" chip.
        stopStream();
        if (activePlanIdRef.current === planId) {
          startPolling(planId);
        }
      },
    });
    sseUnsubscribeRef.current = unsub;
    // Safety net: if the first snapshot doesn't arrive within 8 s the
    // stream is almost certainly being stalled by an intermediary —
    // promote polling so the operator isn't staring at a spinner.
    // Stash the handle so dialog close / replan can cancel it
    // rather than firing against a stale planId.
    sseFirstSnapshotTimerRef.current = setTimeout(() => {
      sseFirstSnapshotTimerRef.current = null;
      if (!opened && activePlanIdRef.current === planId) {
        stopStream();
        startPolling(planId);
      }
    }, 8000);
  }, [stopStream, startPolling, refreshList]);

  const openDetail = useCallback(async (planId) => {
    // Tear down any in-flight transport from a previous open before
    // we change activePlanIdRef — otherwise an arriving snapshot or
    // poll callback would briefly write into the new plan's state.
    stopAllTransports();
    setActivePlanId(planId);
    activePlanIdRef.current = planId;
    setActivePlan(null);
    setTransportMode('idle');
    const detail = await fetchDetail(planId);
    // The await above can be long enough for the user to close the
    // dialog or open a different plan. Bail before kicking off the
    // SSE stream so we don't subscribe to a plan no one's watching.
    if (activePlanIdRef.current !== planId) return;
    if (!detail) return;
    if (TERMINAL_STATUSES.has(detail.status)) {
      // Terminal plan: nothing to subscribe to, the doc we have is
      // the final word. Skip both transports.
      setTransportMode('idle');
      return;
    }
    startStream(planId);
  }, [fetchDetail, startStream, stopAllTransports]);

  const closeDetail = useCallback(() => {
    stopAllTransports();
    setActivePlanId(null);
    activePlanIdRef.current = null;
    setActivePlan(null);
    setTransportMode('idle');
    if (typeof onClearInitialPlan === 'function') onClearInitialPlan();
  }, [stopAllTransports, onClearInitialPlan]);

  const cancelActivePlan = useCallback(async () => {
    if (!activePlanId) return;
    if (!window.confirm('Cancel this plan? Open actions will be marked cancelled but no rollback command is sent.')) {
      return;
    }
    setCancelBusy(true);
    try {
      const updated = await api.cancelPlanRun(activePlanId);
      if (activePlanIdRef.current === activePlanId) {
        setActivePlan(updated);
      }
      enqueueSnackbar('Plan cancelled.', { variant: 'success' });
      stopAllTransports();
      setTransportMode('idle');
      refreshList();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to cancel plan', { variant: 'error' });
    } finally {
      setCancelBusy(false);
    }
  }, [activePlanId, enqueueSnackbar, stopAllTransports, refreshList]);

  const retryActivePlan = useCallback(async () => {
    if (!activePlanId) return;
    setRetryBusy(true);
    try {
      const resp = await api.retryPlanRun(activePlanId);
      const newPlanId = resp?.plan?.id;
      enqueueSnackbar(
        `Retry dispatched: ${resp?.retried_actions ?? '?'} action(s) re-queued.`,
        { variant: 'success' },
      );
      refreshList();
      if (newPlanId) {
        if (typeof onOpenPlan === 'function') {
          // Bubble up so the parent can swap the deep-link target
          // (works the same way as the apply-path flow).
          onOpenPlan(newPlanId);
        } else {
          openDetail(newPlanId);
        }
      }
    } catch (err) {
      const data = err?.data || err?.response;
      if ((err?.status === 409 || err?.statusCode === 409) && data?.plan_id) {
        enqueueSnackbar(
          `Retry blocked — another plan is already running for ${data.serial || 'one of the targeted devices'}. Open it instead.`,
          { variant: 'warning' },
        );
        if (typeof onOpenPlan === 'function') onOpenPlan(data.plan_id);
        else openDetail(data.plan_id);
      } else {
        enqueueSnackbar(err?.message || 'Retry failed', { variant: 'error' });
      }
    } finally {
      setRetryBusy(false);
    }
  }, [activePlanId, enqueueSnackbar, refreshList, openDetail, onOpenPlan]);

  const clearPlanFromLocalState = useCallback((planId) => {
    setPlans((prev) => prev.filter((p) => p.id !== planId));
    setBulkSelection((prev) => prev.filter((id) => id !== planId));
    if (activePlanIdRef.current === planId) {
      stopAllTransports();
      setActivePlanId(null);
      activePlanIdRef.current = null;
      setActivePlan(null);
      setTransportMode('idle');
    }
  }, [stopAllTransports]);

  // Permanently remove a plan from history. Active runs must be cancelled
  // first; orphaned "running" rows after a BE restart can be deleted.
  const deletePlan = useCallback(async (planId, planLabel) => {
    if (!planId) return;
    if (!window.confirm(
      `Delete plan history for "${planLabel}"? This permanently removes the plan record. ` +
      'Devices are not touched.',
    )) {
      return;
    }
    setDeleteBusyId(planId);
    try {
      await api.deletePlanRun(planId);
      clearPlanFromLocalState(planId);
      enqueueSnackbar('Plan deleted.', { variant: 'success' });
    } catch (err) {
      const data = err?.data;
      if (err?.statusCode === 409) {
        enqueueSnackbar(
          `Cannot delete a ${data?.status || 'running'} plan — cancel it first.`,
          { variant: 'warning' },
        );
      } else if (err?.statusCode === 404) {
        clearPlanFromLocalState(planId);
        enqueueSnackbar('Plan was already deleted.', { variant: 'info' });
      } else {
        enqueueSnackbar(err?.message || 'Failed to delete plan run', { variant: 'error' });
      }
    } finally {
      setDeleteBusyId(null);
      refreshList();
    }
  }, [enqueueSnackbar, refreshList, clearPlanFromLocalState]);

  const deletePlansBulk = useCallback(async (planIds) => {
    const targets = (planIds || []).filter(Boolean);
    if (targets.length === 0) return;
    if (!window.confirm(
      `Delete ${targets.length} plan run${targets.length === 1 ? '' : 's'} from history? Devices are not touched.`,
    )) {
      return;
    }
    setBulkDeleteBusy(true);
    let ok = 0;
    let failed = 0;
    for (const planId of targets) {
      const plan = plans.find((p) => p.id === planId);
      const planLabel = plan?.name || `Plan ${planId.slice(0, 8)}`;
      try {
        await api.deletePlanRun(planId);
        clearPlanFromLocalState(planId);
        ok += 1;
      } catch (err) {
        failed += 1;
        if (err?.statusCode === 409) {
          enqueueSnackbar(
            `Cannot delete "${planLabel}" while still running — cancel it first.`,
            { variant: 'warning' },
          );
        } else if (err?.statusCode === 404) {
          clearPlanFromLocalState(planId);
          ok += 1;
        } else {
          enqueueSnackbar(err?.message || `Failed to delete "${planLabel}"`, { variant: 'error' });
        }
      }
    }
    setBulkDeleteBusy(false);
    setBulkSelection([]);
    await refreshList();
    if (ok > 0) {
      enqueueSnackbar(`Deleted ${ok} plan run${ok === 1 ? '' : 's'}.`, { variant: 'success' });
    }
    if (failed > 0 && ok === 0) {
      enqueueSnackbar(`No plan runs were deleted (${failed} failed).`, { variant: 'error' });
    }
  }, [plans, clearPlanFromLocalState, enqueueSnackbar, refreshList]);

  const handleToggleBulkPlan = useCallback((planId) => {
    setBulkSelection((prev) => (
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId]
    ));
  }, []);

  const handleToggleAllBulkPlans = useCallback(() => {
    setBulkSelection((prev) => (
      prev.length === plans.length ? [] : plans.map((p) => p.id)
    ));
  }, [plans]);

  // When the parent passes a deep-link planId (after it created a
  // run from the analyser apply path), open the detail dialog
  // immediately so the operator transitions straight into the live
  // view without an extra click.
  useEffect(() => {
    if (initialPlanId && activePlanIdRef.current !== initialPlanId) {
      openDetail(initialPlanId);
    }
  }, [initialPlanId, openDetail]);

  useEffect(() => () => stopAllTransports(), [stopAllTransports]);

  const summaryProgress = useMemo(() => {
    if (!activePlan) return { done: 0, total: 0, pct: 0 };
    const s = activePlan.summary || {};
    const total = s.total || (activePlan.actions?.length || 0);
    const done = (s.verified || 0) + (s.mismatched || 0) + (s.timed_out || 0) + (s.failed_to_send || 0);
    const pct = total > 0 ? (done / total) * 100 : 0;
    return { done, total, pct };
  }, [activePlan]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Plan runs</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Every &quot;apply&quot; from the analyser or a snapshot diff becomes a plan run. The backend dispatches
              the underlying commands, captures the pre-apply device state, then watches subsequent state
              reports to verify each change actually landed. The detail view subscribes to a live event
              stream while a run is active (falling back to {Math.round(POLL_INTERVAL_MS / 1000)} s polling
              when the stream is unavailable).
              {allowPlanDelete && ' Cancel, Retry, and delete are available for admins; completed plans can be removed from history with the row\'s delete button.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh plan list">
              <span>
                <IconButton onClick={refreshList} disabled={loadingList}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Recent plan runs</Typography>
            {loadingList && <CircularProgress size={18} />}
          </Stack>
          {allowPlanDelete && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={bulkSelection.length === 0 || bulkDeleteBusy || loadingList}
                onClick={() => deletePlansBulk(bulkSelection)}
              >
                Delete selected ({bulkSelection.length})
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={plans.length === 0 || bulkDeleteBusy || loadingList}
                onClick={() => deletePlansBulk(plans.map((p) => p.id))}
              >
                Delete all
              </Button>
            </Stack>
          )}
        </Stack>
        {(!plans || plans.length === 0) && !loadingList && (
          <Alert severity="info" variant="outlined">
            No plan runs yet. Apply an analyser plan or a snapshot diff to start one.
          </Alert>
        )}
        {plans.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {allowPlanDelete && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={plans.length > 0 && bulkSelection.length === plans.length}
                        indeterminate={bulkSelection.length > 0 && bulkSelection.length < plans.length}
                        disabled={plans.length === 0 || bulkDeleteBusy}
                        onChange={handleToggleAllBulkPlans}
                      />
                    </TableCell>
                  )}
                  <TableCell>Plan</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Verified / Total</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((p) => {
                  const meta = STATUS_META[p.status] || STATUS_META.running;
                  const summary = p.summary || {};
                  const verified = summary.verified || 0;
                  const total = summary.total || 0;
                  const isTerminal = TERMINAL_STATUSES.has(p.status);
                  const planLabel = p.name || `Plan ${p.id.slice(0, 8)}`;
                  const deletingThis = deleteBusyId === p.id;
                  return (
                    <TableRow key={p.id} hover>
                      {allowPlanDelete && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={bulkSelection.includes(p.id)}
                            disabled={bulkDeleteBusy || deletingThis}
                            onChange={() => handleToggleBulkPlan(p.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {planLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {p.id.slice(0, 8)}…
                        </Typography>
                      </TableCell>
                      <TableCell>{p.source || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={meta.color}
                          icon={meta.icon}
                          label={meta.label}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {verified}/{total}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatTimestamp(p.created_at)}>
                          <span>{formatRelative(p.created_at)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{p.created_by || '—'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => openDetail(p.id)}
                          >
                            Monitor
                          </Button>
                          {allowPlanDelete && (
                            <Tooltip
                              title={
                                isTerminal
                                  ? 'Delete this plan from history. Devices are not touched.'
                                  : 'Delete stuck/orphaned plan, or cancel an active plan first.'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deletePlan(p.id, planLabel)}
                                  disabled={deletingThis || bulkDeleteBusy}
                                  aria-label={`Delete plan ${planLabel}`}
                                >
                                  {deletingThis ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={Boolean(activePlanId)} onClose={closeDetail} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          Plan run
          {activePlan?.name && (
            <Typography variant="body2" color="text.secondary">
              {activePlan.name}
            </Typography>
          )}
          {!activePlan?.name && activePlanId && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {activePlanId}
            </Typography>
          )}
          <IconButton
            onClick={closeDetail}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetail && !activePlan && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4, justifyContent: 'center' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading plan run…</Typography>
            </Stack>
          )}
          {activePlan && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {(() => {
                  const meta = STATUS_META[activePlan.status] || STATUS_META.running;
                  return (
                    <Chip
                      size="small"
                      icon={meta.icon}
                      label={meta.label}
                      color={meta.color}
                      variant="outlined"
                    />
                  );
                })()}
                <Chip size="small" variant="outlined" label={`Source: ${activePlan.source || '—'}`} />
                <Chip size="small" variant="outlined" label={`Created: ${formatRelative(activePlan.created_at)}`} />
                {isMeaningfulTimestamp(activePlan.started_at) && (
                  <Chip size="small" variant="outlined" label={`Started: ${formatRelative(activePlan.started_at)}`} />
                )}
                {isMeaningfulTimestamp(activePlan.completed_at) && (
                  <Chip size="small" variant="outlined" label={`Completed: ${formatRelative(activePlan.completed_at)}`} />
                )}
                {activePlan.status === 'running' && transportMode !== 'idle' && (
                  <Tooltip
                    title={
                      transportMode === 'sse'
                        ? 'Live event stream — updates pushed the moment the watcher reconciles a state report.'
                        : `Live stream unavailable; polling every ${Math.round(POLL_INTERVAL_MS / 1000)} s.`
                    }
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color={transportMode === 'sse' ? 'success' : 'warning'}
                      icon={transportMode === 'sse' ? <WifiIcon fontSize="small" /> : <WifiOffIcon fontSize="small" />}
                      label={transportMode === 'sse' ? 'Live (SSE)' : 'Polling'}
                    />
                  </Tooltip>
                )}
              </Stack>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Progress: {summaryProgress.done}/{summaryProgress.total}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={summaryProgress.pct}
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Actions ({(activePlan.actions || []).length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Pre-apply</TableCell>
                      <TableCell>Expected</TableCell>
                      <TableCell>Observed</TableCell>
                      <TableCell>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(activePlan.actions || []).flatMap((action) => {
                      const meta = ACTION_STATUS_META[action.status] || { color: 'default', label: action.status || '—' };
                      const expectations = action.expectations && action.expectations.length > 0
                        ? action.expectations
                        : [{ field: '—', expected: '—', observed_value: '—', last_observed_at: null, matched: false, pre_apply_value: '—' }];
                      return expectations.map((exp, idx) => (
                        <TableRow
                          key={`${action.action_id}-${exp.field || idx}`}
                          sx={{
                            // Subtle row tint so the operator can see at a
                            // glance which expectations have already
                            // matched within an action that's still
                            // awaiting other ones.
                            ...(exp.matched && { backgroundColor: 'action.hover' }),
                          }}
                        >
                          {idx === 0 ? (
                            <>
                              <TableCell rowSpan={expectations.length}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                  {describeAction(action)}
                                </Typography>
                                {action.reason && (
                                  <Typography variant="caption" color="text.secondary">
                                    {action.reason}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell rowSpan={expectations.length}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={meta.color}
                                  label={meta.label}
                                />
                              </TableCell>
                            </>
                          ) : null}
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{exp.field}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(exp.pre_apply_value)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(exp.expected)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(exp.observed_value)}</TableCell>
                          <TableCell>
                            {(() => {
                              const ts = pickFirstMeaningfulTimestamp(
                                exp.last_observed_at,
                                action.verified_at,
                                action.completed_at,
                              );
                              if (!ts) {
                                return <Typography variant="caption" color="text.secondary">—</Typography>;
                              }
                              return (
                                <Tooltip title={formatTimestamp(ts)}>
                                  <span>{formatRelative(ts)}</span>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      ));
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Tooltip
            title={
              transportMode === 'sse' && activePlan?.status === 'running'
                ? 'Updates arrive automatically via the live stream.'
                : 'Fetch the latest plan state from the server.'
            }
          >
            <span>
              <Button
                onClick={() => fetchDetail(activePlanId)}
                disabled={
                  !activePlanId
                  || loadingDetail
                  || (transportMode === 'sse' && activePlan?.status === 'running')
                }
              >
                Refresh now
              </Button>
            </span>
          </Tooltip>
          {allowPlanDelete && activePlan?.status === 'running' && (
            <Button
              color="warning"
              startIcon={<StopCircleIcon />}
              onClick={cancelActivePlan}
              disabled={cancelBusy}
            >
              {cancelBusy ? 'Cancelling…' : 'Cancel plan'}
            </Button>
          )}
          {allowPlanDelete && activePlan && TERMINAL_STATUSES.has(activePlan.status) &&
            (activePlan.actions || []).some((a) => RETRIABLE_ACTION_STATUSES.has(a.status)) && (
              <Tooltip title="Re-dispatch the failed/timed_out/cancelled actions as a new plan run.">
                <span>
                  <Button
                    color="primary"
                    startIcon={<ReplayIcon />}
                    onClick={retryActivePlan}
                    disabled={retryBusy}
                  >
                    {retryBusy ? 'Retrying…' : 'Retry failed'}
                  </Button>
                </span>
              </Tooltip>
            )}
          {allowPlanDelete && activePlan && TERMINAL_STATUSES.has(activePlan.status) && activePlanId && (
            <Tooltip title="Delete this plan from history. Devices are not touched.">
              <span>
                <Button
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => deletePlan(activePlanId, activePlan.name || `Plan ${activePlanId.slice(0, 8)}`)}
                  disabled={deleteBusyId === activePlanId}
                >
                  {deleteBusyId === activePlanId ? 'Deleting…' : 'Delete'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlansPanel;
